import type {
  AlarmException,
  AlarmOverride,
  ArmedResponse,
  AlarmRoutine,
  AlarmsListResponse,
  RoutineSuggestion,
  AlarmExceptionSuggestion,
} from "./types";
import { apiRequest } from "./request";
import { AlarmConfiguration } from "@visioni/a3s-alarm-schedules/schema";

// Alarms API
export const alarmsApi = {
  async getByCamera(tenantId: string, siteId: string, cameraKey: string): Promise<AlarmConfiguration> {
    const data = await apiRequest<AlarmConfiguration>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms`,
      logMessage: "Fetching alarm for camera:",
    });

    // console.log("Alarm detail returned from API:", JSON.stringify(data, null, 2));
    return data;
  },

  async list(tenantId: string, siteId: string): Promise<AlarmConfiguration[]> {
    const data = await apiRequest<AlarmsListResponse>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/alarms`,
      logMessage: "Fetching alarms from:",
    });

    return data.items;
  },

  async updateResponse(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    response: ArmedResponse,
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/response`,
      body: { response },
      logMessage: "Updating alarm response at:",
    });
  },

  async updateRoutine(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    routine: AlarmRoutine,
    timezone: string,
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/routine`,
      body: { routine, timezone },
    });
  },

  async updateExceptions(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    exceptions: AlarmException[],
    timezone: string,
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exceptions`,
      body: { exceptions, timezone },
      logMessage: "Updating alarm exceptions at:",
    });
  },

  async updateOverride(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    override: AlarmOverride | null,
    timezone: string,
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/override`,
      body: { override, timezone },
      logMessage: "Updating alarm override at:",
    });
  },

  async updateSettings(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    body: {
      response?: ArmedResponse;
      routine?: AlarmRoutine;
      exceptions?: AlarmException[];
      timezone?: string;
    },
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms`,
      body,
    });
  },

  async updateTimezone(
    tenantId: string,
    siteId: string,
    cameraKey: string,
    timezone: string,
  ): Promise<AlarmConfiguration> {
    return apiRequest<AlarmConfiguration>({
      method: "POST",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/timezone`,
      body: { timezone },
      logMessage: "Updating alarm timezone at:",
    });
  },

  async getRoutineSuggestions(tenantId: string, siteId: string, cameraKey: string): Promise<RoutineSuggestion[]> {
    return apiRequest<RoutineSuggestion[]>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/routine/suggestions`,
      logMessage: "Fetching routine suggestions from:",
    });
  },

  async getExceptionSuggestions(
    tenantId: string,
    siteId: string,
    cameraKey: string,
  ): Promise<AlarmExceptionSuggestion[]> {
    return apiRequest<AlarmExceptionSuggestion[]>({
      method: "GET",
      path: `/api/v1/tenants/${tenantId}/sites/${siteId}/cameras/${cameraKey}/alarms/exception-suggestions`,
      logMessage: "Fetching exception suggestions from:",
    });
  },
};
